import React, { useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Divider } from '@mui/material';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { find } from 'lodash';
import Grid from '@mui/material/Grid';
import DoneIcon from '@mui/icons-material/Done';
import CustomButton from 'components/Button/CustomButton';
import CustomInput from 'components/Form/TextField/TextField.cmp';
import { useSnackbar } from 'notistack';
import { updateProfileApi } from 'api/userApi';
import { useWorkspaceHook } from 'utils/CustomHooks/useWorkspaceHook';
import {
  CTypographyProfile,
  LabelFieldProfile,
} from 'pages/my-settings/styles';
import PhoneEmailInput from 'components/Dropdown/PhoneCodeInput';
import { getCountryCode, getNumberWithoutCode } from '../../helper/phoneNumber';
import actions from 'store/actions';
const MyProfileForm = ({ userProfileData, picCdnPath, setPicCdnPath }) => {
  const { workspaceId } = useWorkspaceHook();
  const {
    fullName,
    emailId,
    phoneNo,
    id: userId,
    UserHotels,
  } = userProfileData;
  const currentUserHotel = find(
    UserHotels,
    (userHotel) => userHotel.Hotel.id === workspaceId,
  );
  const { title } = currentUserHotel || {};
  const dispatch = useDispatch();
  const formikRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  const [phoneCode, setPhoneCode] = useState<any>(null);
  const selectedPhoneCode = phoneNo && getCountryCode(phoneNo);
  const phoneNumberWithoutCode = phoneNo && getNumberWithoutCode(phoneNo);
  const initialValues = {
    fullName: fullName,
    phone: phoneNumberWithoutCode,
    email: emailId,
    position: title,
  };

  const validationSchemaModel = Yup.object({
    fullName: Yup.string()
      .max(80, 'Must be 80 characters or less')
      .required('Please provide valid full name (first & last)')
      .nullable(),
    phone: Yup.string()
      .matches(/^[+]*[0-9]{5,14}$/, 'Please enter valid phone number')
      .nullable(),
    email: Yup.string()
      .email('Please provide valid email address')
      .nullable()
      .test('require phone or email', function (email) {
        const phone = this.resolve(Yup.ref('phone'));
        if (!email && !phone) {
          return this.createError({
            message: `Please provide valid email address or phone`,
            path: 'email',
          });
        } else return true;
      }),
    position: Yup.string().nullable(),
  });

  const handleSubmit = async (values, resetForm) => {
    const postObj = {
      ...values,
      phone: values?.phone ? phoneCode?.obj?.dial_code + values?.phone : null,
    };
    const response = await updateProfileApi(userId, postObj, picCdnPath);
    if (response) {
      setPicCdnPath('');
      resetForm({
        values: {
          fullName: values.fullName,
          phone: values.phone,
          email: values.email,
          position: values.position,
        },
      });
    }
    dispatch(actions.reloadUsers());
  };
  const handlePhoneCodeSelect = (value) => {
    setPhoneCode(value);
  };
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchemaModel}
      validateOnChange={false}
      validateOnBlur={false}
      innerRef={formikRef}
      onSubmit={async (values, formikHelpers) => {
        const { setSubmitting, resetForm } = formikHelpers;
        await handleSubmit(values, resetForm);
        setSubmitting(false);
      }}
    >
      {(props) => {
        const { touched, values, errors, getFieldProps, dirty, isSubmitting } =
          props;
        return (
          <Form autoComplete="off">
            <Grid container spacing={0}>
              <Grid item xs={12}>
                <CTypographyProfile>Profile Details</CTypographyProfile>
                <Divider sx={{ pt: 2, mb: 3 }} />
              </Grid>
              <Grid item xs={3}>
                <LabelFieldProfile htmlFor="fullName">
                  Full Name
                </LabelFieldProfile>
              </Grid>
              <Grid item xs={9}>
                <Field name="fullName">
                  {(props) => {
                    return (
                      <CustomInput
                        suppressErrorSpace={true}
                        error={touched.fullName && errors.fullName}
                        sx={{ mb: '10px' }}
                        fieldProps={{
                          ...props.field,
                          sx: { width: 320 },
                          ...getFieldProps('fullName'),
                        }}
                      />
                    );
                  }}
                </Field>
              </Grid>
              <Grid item xs={3}>
                <LabelFieldProfile htmlFor="phone">Phone</LabelFieldProfile>
              </Grid>
              <Grid item xs={9}>
                <Field name="phone">
                  {(props) => (
                    <PhoneEmailInput
                      suppressEmail={true}
                      error={touched.phone && errors.phone}
                      onPhoneCodeSelect={handlePhoneCodeSelect}
                      selectedCode={selectedPhoneCode}
                      formControlProps={{
                        suppressErrorSpace: true,
                        sx: { mb: '10px' },
                      }}
                      fieldProps={{
                        ...props.field,
                        sx: { width: 320 },
                        ...getFieldProps('phone'),
                      }}
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={3}>
                <LabelFieldProfile>Email</LabelFieldProfile>
              </Grid>
              <Grid item xs={9}>
                <Field name="email">
                  {(props) => (
                    <CustomInput
                      suppressErrorSpace={true}
                      error={touched.email && errors.email}
                      sx={{ mb: '10px' }}
                      fieldProps={{
                        ...props.field,
                        sx: { width: 320 },
                        placeholder: 'Enter email address',
                        ...getFieldProps('email'),
                      }}
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={3}>
                <LabelFieldProfile>Position</LabelFieldProfile>
              </Grid>
              <Grid item xs={9}>
                <Field name="position">
                  {(props) => (
                    <CustomInput
                      suppressErrorSpace={true}
                      error={touched.position && errors.position}
                      sx={{ mb: '10px' }}
                      fieldProps={{
                        ...props.field,
                        sx: { width: 320 },
                        placeholder: 'Enter your position',
                        ...getFieldProps('position'),
                      }}
                    />
                  )}
                </Field>
              </Grid>
              <Grid item xs={12} sx={{ my: 2 }}>
                {(dirty || picCdnPath !== '') && (
                  <CustomButton
                    variant="contained"
                    type="submit"
                    startIcon={<DoneIcon />}
                    buttonLoader={isSubmitting}
                  >
                    Save Changes
                  </CustomButton>
                )}
              </Grid>
            </Grid>
          </Form>
        );
      }}
    </Formik>
  );
};

export default MyProfileForm;
